import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Check, ZoomIn, X } from 'lucide-react';

// Zelle tutorial images
import zelleStep1 from '../../../assets/images/zelle/1.webp';
import zelleStep2 from '../../../assets/images/zelle/3.webp';
import zelleStep3 from '../../../assets/images/zelle/4.webp';
import zelleStep4 from '../../../assets/images/zelle/5.webp';

interface TutorialSlide {
    id: number;
    image: string;
    step: string;
    text: string;
    color: string;
}

const ZellePayment: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { amount, accountCode } = location.state || { amount: '0', accountCode: '' };

    const [currentSlide, setCurrentSlide] = useState(0);
    const [showText, setShowText] = useState(true);
    const [zoomModalVisible, setZoomModalVisible] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    const tutorialSlides: TutorialSlide[] = [
        {
            id: 1,
            image: zelleStep1,
            step: '1',
            text: 'Ouvrez "Pay & Transfer" dans votre app bancaire et cherchez l\'option Zelle',
            color: '#3b82f6'
        },
        {
            id: 2,
            image: zelleStep2,
            step: '2',
            text: 'Allez dans "Select Recipient" pour choisir le contact Bousanm (s\'il n\'est pas déjà là)',
            color: '#8b5cf6'
        },
        {
            id: 3,
            image: zelleStep3,
            step: '3',
            text: 'Ajoutez le contact Bousanm: zig.bousanm@gmail.com ou +1 954 831-9103',
            color: '#10b981'
        },
        {
            id: 4,
            image: zelleStep4,
            step: '4',
            text: `Entrez le montant ($${parseFloat(amount).toFixed(2)}) et dans "Memo" écrivez votre numéro de compte Bousanm (ex: ${accountCode || '500848364'})`,
            color: '#f59e0b'
        }
    ];

    const handleNext = () => {
        if (currentSlide < tutorialSlides.length - 1) {
            setCurrentSlide(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentSlide > 0) {
            setCurrentSlide(prev => prev - 1);
        }
    };

    const openZoom = (image: string) => {
        setSelectedImage(image);
        setZoomModalVisible(true);
    };

    const currentSlideData = tutorialSlides[currentSlide];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#6C1ED9] to-[#9333ea] text-white">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate(-1)}
                            className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="text-center">
                            <h1 className="text-lg font-bold">Payer via Zelle</h1>
                            <p className="text-xs text-white/80">Tutoriel étape par étape</p>
                        </div>
                        <div className="bg-white/25 px-3 py-1.5 rounded-xl">
                            <span className="text-sm font-bold">{currentSlide + 1}/{tutorialSlides.length}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Carousel Content */}
            <div className="max-w-4xl mx-auto px-4 py-6">
                <div
                    key={currentSlide}
                    className="relative animate-fade-in"
                >
                    {/* Image Card */}
                    <div
                        className="relative bg-white/70 backdrop-blur-md rounded-3xl p-3 border border-white/50 shadow-xl cursor-pointer transition-all duration-300"
                        onClick={() => setShowText(!showText)}
                        onDoubleClick={() => openZoom(currentSlideData.image)}
                    >
                        <img
                            src={currentSlideData.image}
                            alt={`Étape ${currentSlideData.step}`}
                            className="w-full h-[400px] md:h-[500px] object-contain rounded-2xl bg-gray-100"
                        />

                        {/* Floating Card Overlay */}
                        {showText && (
                            <div className="absolute bottom-6 left-6 right-6 animate-slide-up">
                                <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-4 border border-white/60 shadow-lg">
                                    <div
                                        className="inline-block px-3 py-1 rounded-lg mb-2 text-white text-xs font-bold"
                                        style={{ backgroundColor: currentSlideData.color }}
                                    >
                                        Étape {currentSlideData.step}
                                    </div>
                                    <p className="text-gray-800 text-sm leading-relaxed font-medium">
                                        {currentSlideData.text}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Tap to show indicator */}
                        {!showText && (
                            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2">
                                <span>Tapez pour afficher</span>
                            </div>
                        )}
                    </div>

                    {/* Zoom hint */}
                    {showText && (
                        <div className="flex items-center justify-center gap-2 mt-4 text-[#6C1ED9]">
                            <ZoomIn className="w-4 h-4" />
                            <span className="text-sm">Double-cliquez pour zoomer</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Navigation */}
            <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-[#6C1ED9]/10 px-4 py-4">
                <div className="max-w-4xl mx-auto">
                    {/* Dots */}
                    <div className="flex justify-center gap-2 mb-4">
                        {tutorialSlides.map((_, index) => (
                            <div
                                key={index}
                                className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide
                                    ? 'w-6 bg-[#6C1ED9]'
                                    : 'w-2 bg-gray-300'
                                    }`}
                            />
                        ))}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex items-center justify-between">
                        {currentSlide > 0 ? (
                            <button
                                onClick={handlePrevious}
                                className="w-12 h-12 rounded-xl border-2 border-[#6C1ED9] flex items-center justify-center text-[#6C1ED9] hover:bg-[#6C1ED9]/10 transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                        ) : (
                            <div className="w-12" />
                        )}

                        <div className="flex-1" />

                        {currentSlide < tutorialSlides.length - 1 ? (
                            <button
                                onClick={handleNext}
                                className="flex items-center gap-2 bg-[#6C1ED9] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#5C16C5] transition-colors"
                            >
                                <span>Suivant</span>
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-600 transition-colors"
                            >
                                <span>Terminer</span>
                                <Check className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Zoom Modal */}
            {zoomModalVisible && selectedImage && (
                <div
                    className="fixed inset-0 bg-black z-50 flex items-center justify-center animate-fade-in"
                    onClick={() => setZoomModalVisible(false)}
                >
                    <button
                        onClick={() => setZoomModalVisible(false)}
                        className="absolute top-6 right-6 text-white z-10"
                    >
                        <X className="w-10 h-10" />
                    </button>
                    <img
                        src={selectedImage}
                        alt="Zoomed"
                        className="max-w-full max-h-full object-contain"
                    />
                    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 bg-black/60 text-white/70 px-4 py-2 rounded-full text-sm">
                        Cliquez n'importe où pour fermer
                    </div>
                </div>
            )}

            {/* CSS animations */}
            <style>{`
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slide-up {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fade-in 0.3s ease-out;
                }
                .animate-slide-up {
                    animation: slide-up 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default ZellePayment;
